/**
 * 1Password's one setting: the account's sign-in address, stored as `config_json.base_url`.
 *
 * Cortex opens every 1Password browser run at this address, so it is the team's own console
 * origin (`https://acme.1password.com`, `https://acme.1password.eu`, ...). The host is deliberately
 * not checked against 1Password's domains -- those can change -- only that it is an https URL.
 * There is nothing to test it against, so both surfaces save it without a Test.
 */
export const ONEPASSWORD_APP = "onepassword";
export const ONEPASSWORD_BASE_URL_KEY = "base_url";

const INVALID_MESSAGE = "Enter the full sign-in address, starting with https://";

export type SignInAddressResult = { ok: true; value: string } | { ok: false; error: string };

/**
 * The address reduced to its bare origin: `https://Acme.1Password.com/home/` becomes
 * `https://acme.1password.com`. A value typed without a scheme gets `https://`; any other scheme
 * is refused rather than rewritten.
 */
export function normalizeOnePasswordSignInAddress(raw: string): SignInAddressResult {
  const trimmed = (raw || "").trim();
  if (!trimmed) return { ok: false, error: "Sign-in address is required" };

  const withScheme = /^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
  let url: URL;
  try {
    url = new URL(withScheme);
  } catch {
    return { ok: false, error: INVALID_MESSAGE };
  }
  if (url.protocol !== "https:" || !url.hostname || url.username || url.password) {
    return { ok: false, error: INVALID_MESSAGE };
  }
  return { ok: true, value: url.origin };
}
